// Çatdırılma platformaları (Wolt, Bolt Food) — terminalın qəbul paneli üçün
// pos_local gateway-i.
//
// Sifariş buludda yaranır (platformanın webhook-u), `live_orders` axını ilə
// pos_local-a düşür və kassir QƏBUL edənə qədər mətbəxə getmir. Qəbul/rədd
// qərarı pos_local-dan buluda, oradan da platformaya çatdırılır.

import { posRequest } from './posClient';

export type DeliveryProvider = 'wolt' | 'bolt' | 'fake' | string;
export type DeliveryStatus = 'incoming' | 'accepted' | 'rejected' | 'ready' | 'picked_up' | 'cancelled';

/** Platformanın qəbul etdiyi rədd səbəbləri — pos_local ilə eyni dəst. */
export const REJECT_REASONS = ['too_busy', 'item_unavailable', 'closing_soon', 'other'] as const;
export type RejectReason = (typeof REJECT_REASONS)[number];

/** Hazırlıq müddəti seçimləri (dəqiqə). */
export const PREP_CHOICES = [10, 15, 20, 30, 45] as const;

export interface DeliveryOrderItem {
  name: string;
  quantity: number;
  unitPriceCents: number;
  notes: string | null;
  modifiers: string | null;
}

export interface DeliveryOrder {
  orderId: string;
  cloudOrderId: string;
  provider: DeliveryProvider;
  externalOrderId: string | null;
  displayId: string | null;
  status: DeliveryStatus;
  type: string;
  scheduledFor: number | null;
  acceptDeadline: number | null;
  prepMinutes: number | null;
  defaultPrep: number;
  customerName: string | null;
  customerPhone: string | null;
  platformTotalCents: number;
  cancelReason: string | null;
  receivedAt: number;
  updatedAt: number;
  items: DeliveryOrderItem[];
}

export class DeliveryGatewayError extends Error {
  constructor(public readonly code: string, message: string) {
    super(message);
  }
}

function unwrap<T>(res: { ok: boolean; data?: T; error?: { code: string; message: string } }): T {
  if (res.ok && res.data !== undefined) return res.data;
  throw new DeliveryGatewayError(res.error?.code ?? 'UNKNOWN', res.error?.message ?? 'Request failed');
}

export async function listDeliveryOrders(): Promise<DeliveryOrder[]> {
  return unwrap(await posRequest<DeliveryOrder[]>({ method: 'GET', path: '/v1/delivery/orders' }));
}

export async function acceptDeliveryOrder(orderId: string, prepMinutes: number): Promise<DeliveryOrder> {
  return unwrap(
    await posRequest<DeliveryOrder>({
      method: 'POST',
      path: `/v1/delivery/orders/${encodeURIComponent(orderId)}/accept`,
      body: { prepMinutes },
    }),
  );
}

export async function rejectDeliveryOrder(orderId: string, reason: RejectReason): Promise<DeliveryOrder> {
  return unwrap(
    await posRequest<DeliveryOrder>({
      method: 'POST',
      path: `/v1/delivery/orders/${encodeURIComponent(orderId)}/reject`,
      body: { reason },
    }),
  );
}

export async function markDeliveryOrderReady(orderId: string): Promise<DeliveryOrder> {
  return unwrap(
    await posRequest<DeliveryOrder>({
      method: 'POST',
      path: `/v1/delivery/orders/${encodeURIComponent(orderId)}/ready`,
      body: {},
    }),
  );
}

/** Platforma adı ekranda. */
export function providerLabel(provider: DeliveryProvider): string {
  switch (provider) {
    case 'wolt':
      return 'Wolt';
    case 'bolt':
      return 'Bolt Food';
    case 'fake':
      return 'Test';
    default:
      return provider;
  }
}

/** Qəbul üçün qalan saniyə; son an yoxdursa null, keçibsə 0. */
export function secondsLeft(order: Pick<DeliveryOrder, 'acceptDeadline'>, now: number): number | null {
  if (!order.acceptDeadline) return null;
  return Math.max(0, Math.floor((order.acceptDeadline - now) / 1000));
}

/**
 * Hazırlıq müddəti düymələri. Restoranın admin paneldəki standart dəyəri
 * (məs. 25) siyahıda yoxdursa, o da əlavə olunur — kassir onu bir toxunuşla
 * seçə bilsin.
 */
export function prepChoices(order: Pick<DeliveryOrder, 'defaultPrep'>): number[] {
  const d = order.defaultPrep > 0 ? order.defaultPrep : 15;
  return Array.from(new Set<number>([...PREP_CHOICES, d])).sort((a, b) => a - b);
}
